// Apple-style palette for activity chips. No gray / lila / brown.
// Each tone: light bg (near-white tint) + dark bg + saturated edge line + readable ink.
export type AppTone = {
  id: string;
  name: string;
  hue: number;
  bg: string;
  bgDark: string;
  line: string;
  ink: string;
  inkDark: string;
  swatch: string;
};

function tone(id: string, name: string, h: number): AppTone {
  return {
    id, name, hue: h,
    // Muted Apple-Notes / Things-App feel: near-white paper bg with a colored edge.
    bg:      `oklch(0.97 0.018 ${h})`,
    bgDark:  `oklch(0.28 0.055 ${h})`,
    line:    `oklch(0.58 0.095 ${h})`,
    ink:     `oklch(0.38 0.10 ${h})`,
    inkDark: `oklch(0.92 0.045 ${h})`,
    swatch:  `oklch(0.68 0.115 ${h})`,
  };
}

export const APP_PALETTE: AppTone[] = [
  tone('blue',    'Blau',    245),
  tone('cyan',    'Cyan',    215),
  tone('teal',    'Teal',    195),
  tone('mint',    'Mint',    170),
  tone('green',   'Grün',    148),
  tone('lime',    'Lime',    125),
  tone('yellow',  'Gelb',     92),
  tone('amber',   'Amber',    75),
  tone('orange',  'Orange',   55),
  tone('coral',   'Koralle',  28),
  tone('rose',    'Rose',     12),
  tone('pink',    'Pink',    355),
];

export const TONE_BY_ID = Object.fromEntries(APP_PALETTE.map((t) => [t.id, t])) as Record<string, AppTone>;

// ─── Site / App label extraction ───────────────────────────────────────
// Browsers: derive the site from the title. Everything else: strip .app suffix.
// Titles from Chrome look like:
//   "Page Title - Site Name - Google Chrome – Tom (Tom Embke Privat)"
//   "(1) Instagram • Messages - Google Chrome – ..."
//   "Claude - Google Chrome – ..."

const BROWSER_RX = /^(google chrome|chrome|safari|firefox|arc|brave|microsoft edge|edge)$/i;

const SITE_PATTERNS: [RegExp, string][] = [
  [/claude/i, 'Claude'],
  [/chatgpt|openai/i, 'ChatGPT'],
  [/gemini\.google|bard\.google/i, 'Gemini'],
  [/\byoutube\b/i, 'YouTube'],
  [/\bnetflix\b/i, 'Netflix'],
  [/\blinkedin\b/i, 'LinkedIn'],
  [/\bgithub\b/i, 'GitHub'],
  [/\bgitlab\b/i, 'GitLab'],
  [/stack\s?overflow/i, 'Stack Overflow'],
  [/\bnotion\b/i, 'Notion'],
  [/\bfigma\b/i, 'Figma'],
  [/\bvercel\b/i, 'Vercel'],
  [/cloudflare/i, 'Cloudflare'],
  [/gmail|google mail/i, 'Gmail'],
  [/google (docs?|sheets?|slides?|drive|meet|calendar|kalender)/i, 'Google Workspace'],
  [/\binstagram\b/i, 'Instagram'],
  [/twitter|\bx\.com\b|\bX – /i, 'X'],
  [/\breddit\b/i, 'Reddit'],
  [/whatsapp/i, 'WhatsApp'],
  [/\bdiscord\b/i, 'Discord'],
  [/\bslack\b/i, 'Slack'],
  [/spotify/i, 'Spotify'],
  [/\bmedium\b/i, 'Medium'],
  [/hacker news|ycombinator/i, 'Hacker News'],
  [/anthropic\.com/i, 'Anthropic'],
  [/producthunt/i, 'Product Hunt'],
  [/amazon\.|\bamazon\b/i, 'Amazon'],
  [/\bebay\b/i, 'eBay'],
  [/wikipedia/i, 'Wikipedia'],
];

export function extractSite(title: string | null): string | null {
  if (!title) return null;
  // 1. Clean surrounding noise: leading "(1) " notification counts, trailing " - Chrome – Profile".
  let t = title
    .replace(/^\(\d+\)\s*/, '')
    .replace(/\s*[–-]\s*(?:Google\s+)?(?:Chrome|Safari|Firefox|Arc|Brave|Edge)(?:\s*[–-]\s*.*)?$/i, '')
    .trim();

  // 2. Explicit brand patterns — highest priority.
  for (const [rx, name] of SITE_PATTERNS) if (rx.test(t)) return name;

  // 3. Pipe-split ("Overview | Recruiter Tracker") — brand is usually the LAST segment.
  const pipeParts = t.split(/\s*[|·]\s*/).map((s) => s.trim()).filter(Boolean);
  if (pipeParts.length >= 2) {
    const tail = pipeParts[pipeParts.length - 1];
    if (tail.length <= 28) return tail;
  }

  // 4. Dash-split ("Google Kalender - Woche …") — brand is usually the FIRST segment.
  const dashParts = t.split(/\s*[–-]\s*/).map((s) => s.trim()).filter(Boolean);
  if (dashParts.length >= 2) {
    const head = dashParts[0];
    if (head.length <= 28) return head;
  }

  // 5. Plain, short title = its own brand.
  if (t.length > 0 && t.length <= 28) return t;
  return null;
}

export function labelFor(app: string | null | undefined, title: string | null | undefined): string {
  if (!app) return 'Unbekannt';
  if (BROWSER_RX.test(app)) {
    const site = extractSite(title ?? null);
    if (site) return site;
    return 'Web';
  }
  return app.replace(/\.app$/i, '').trim();
}

// ─── Stable color assignment ──────────────────────────────────────────
// Known labels map to fixed tones so colors are stable across sessions.
// Unknown labels fall back to FNV-1a hash mod palette length.
const FIXED_COLORS: Record<string, string> = {
  // Browsers / Web platforms
  Claude: 'coral',
  ChatGPT: 'green',
  Gemini: 'blue',
  YouTube: 'rose',
  Netflix: 'rose',
  LinkedIn: 'blue',
  GitHub: 'teal',
  GitLab: 'orange',
  'Stack Overflow': 'amber',
  Notion: 'yellow',
  Figma: 'pink',
  Vercel: 'cyan',
  Cloudflare: 'orange',
  Gmail: 'rose',
  'Google Workspace': 'blue',
  Instagram: 'pink',
  X: 'cyan',
  Reddit: 'coral',
  WhatsApp: 'green',
  Discord: 'blue',
  Slack: 'amber',
  Spotify: 'lime',
  Medium: 'mint',
  'Hacker News': 'coral',
  Anthropic: 'coral',
  'Product Hunt': 'coral',
  Amazon: 'amber',
  eBay: 'rose',
  Wikipedia: 'teal',
  Web: 'cyan',

  // Native apps
  'Google Chrome': 'cyan',
  Safari: 'cyan',
  Firefox: 'orange',
  Arc: 'rose',
  Brave: 'orange',
  Warp: 'teal',
  iTerm: 'teal',
  iTerm2: 'teal',
  Terminal: 'teal',
  Ghostty: 'mint',
  'Visual Studio Code': 'blue',
  Code: 'blue',
  Cursor: 'blue',
  Xcode: 'blue',
  'Android Studio': 'green',
  IntelliJ: 'rose',
  WebStorm: 'rose',
  PyCharm: 'green',
  Finder: 'cyan',
  Notizen: 'yellow',
  Notes: 'yellow',
  Mail: 'blue',
  Kalender: 'rose',
  Calendar: 'rose',
  Systemeinstellungen: 'mint',
  'System Settings': 'mint',
  Messages: 'green',
  iMessage: 'green',
  Nachrichten: 'green',
  '‎WhatsApp': 'green',
  Musik: 'pink',
  Music: 'pink',
  TV: 'rose',
  Zoom: 'blue',
  Teams: 'blue',
  'Microsoft Teams': 'blue',
  Webex: 'teal',
  Sketch: 'amber',
  Preview: 'teal',
  Vorschau: 'teal',
  'Focus Coach': 'orange',
  Unbekannt: 'cyan',
  Privat: 'mint',
};

function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function colorIdForLabel(label: string, overrides: Record<string, string>): string {
  const fromOverride = overrides[label];
  if (fromOverride && TONE_BY_ID[fromOverride]) return fromOverride;
  const fixed = FIXED_COLORS[label];
  if (fixed && TONE_BY_ID[fixed]) return fixed;
  const idx = fnv1a(label) % APP_PALETTE.length;
  return APP_PALETTE[idx].id;
}

export function toneForLabel(label: string, overrides: Record<string, string>): AppTone {
  return TONE_BY_ID[colorIdForLabel(label, overrides)];
}


// Score color: soft pastel gradient 0..100, snapped to multiples of 5.
// Hue spread keeps 70..100 distinct (yellow -> orange -> red) instead of all-red.
export function scoreTone(score: number) {
  const s = Math.max(0, Math.min(100, Math.round(score / 5) * 5));
  const t = s / 100;
  // Piecewise hue ramp:
  // 0=teal(200) -> 30=mint(160) -> 50=lime(115) -> 70=yellow(85) -> 85=orange(50) -> 100=red(25)
  const hue =
    t <= 0.30 ? 200 + (160 - 200) * (t / 0.30) :
    t <= 0.50 ? 160 + (115 - 160) * ((t - 0.30) / 0.20) :
    t <= 0.70 ? 115 + ( 85 - 115) * ((t - 0.50) / 0.20) :
    t <= 0.85 ?  85 + ( 50 -  85) * ((t - 0.70) / 0.15) :
                 50 + ( 25 -  50) * ((t - 0.85) / 0.15);
  // Stay pastel: L barely moves, C stays low. Hue does the distinguishing.
  const bgL = 0.95 - 0.02 * t;   // 0.95 -> 0.93
  const bgC = 0.05 + 0.03 * t;   // 0.05 -> 0.08
  const fgL = 0.45;
  const fgC = 0.13 + 0.04 * t;   // 0.13 -> 0.17
  const dotL = 0.58 - 0.02 * t;  // 0.58 -> 0.56
  const dotC = 0.16 + 0.03 * t;  // 0.16 -> 0.19
  const bgDarkL = 0.38 + 0.02 * t;
  const bgDarkC = 0.09 + 0.04 * t;
  const h = hue.toFixed(1);
  return {
    bg: `oklch(${bgL.toFixed(3)} ${bgC.toFixed(3)} ${h})`,
    fg: `oklch(${fgL.toFixed(2)} ${fgC.toFixed(3)} ${h})`,
    dot: `oklch(${dotL.toFixed(3)} ${dotC.toFixed(3)} ${h})`,
    bgDark: `oklch(${bgDarkL.toFixed(3)} ${bgDarkC.toFixed(3)} ${h})`,
  };
}
